import { useState } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { Trash2, LogOut, Plus, Check } from 'lucide-react';
import { useTodos, useAddTodo, useToggleTodo, useRemoveTodo } from '@/hooks/use-todos';
import type { Id } from '../../../convex/_generated/dataModel';

export default function TodosPage() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { data: todos, isLoading } = useTodos();
  const { mutateAsync: addTodo, isPending: isAdding } = useAddTodo();
  const { mutateAsync: toggleTodo } = useToggleTodo();
  const { mutateAsync: removeTodo } = useRemoveTodo();
  const [text, setText] = useState('');

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    await addTodo(text);
    setText('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-900">My Todos</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600">{user?.primaryEmailAddress?.emailAddress}</span>
          <button
            onClick={() => signOut()}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-8">
        {/* Add todo form */}
        <form onSubmit={handleAdd} className="flex gap-2 mb-6">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a todo..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isAdding}
          />
          <button
            type="submit"
            disabled={!text.trim() || isAdding}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </form>

        {/* Todo list */}
        {isLoading ? (
          <div className="text-center py-12 text-gray-400 text-sm">Loading...</div>
        ) : todos && todos.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">No todos yet. Add one above!</div>
        ) : (
          <ul className="space-y-2">
            {todos?.map((todo) => (
              <li
                key={todo._id}
                className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg border border-gray-200 group"
              >
                <button
                  onClick={() => toggleTodo(todo._id as Id<'todos'>)}
                  className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    todo.completed
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'border-gray-300 hover:border-green-400'
                  }`}
                >
                  {todo.completed && <Check className="w-3 h-3" />}
                </button>
                <span
                  className={`flex-1 text-sm ${
                    todo.completed ? 'line-through text-gray-400' : 'text-gray-800'
                  }`}
                >
                  {todo.text}
                </span>
                <button
                  onClick={() => removeTodo(todo._id as Id<'todos'>)}
                  className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Stats */}
        {todos && todos.length > 0 && (
          <p className="text-xs text-gray-400 text-center mt-4">
            {todos.filter((t) => t.completed).length} of {todos.length} completed
          </p>
        )}
      </main>
    </div>
  );
}
